import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { Dialog } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Textarea } from '@/components/ui/Textarea';
import { Card } from '@/components/ui/Card';
import { displayPhDate, displayPhTime } from '@/lib/phDate';
import { useTargetPondName } from './useTargetPondName';
import { Check, X, ArrowRight } from 'lucide-react';
import type { EditRequest } from '@/types/edit-request';
import { formatTableName, formatFieldName, getTargetDetail } from '@/types/edit-request';

const UNAFFECTED_BY_APPROVAL = ['pond-names', 'farms', 'membership', 'profile'];

interface RequestDetailModalProps {
  request: EditRequest;
  onClose: () => void;
  onResolved: () => void;
}

interface ChangedField {
  name: string;
  original: unknown;
  proposed: unknown;
}

function getChangedFields(
  original: Record<string, unknown>,
  proposed: Record<string, unknown>
): ChangedField[] {
  const changes: ChangedField[] = [];

  for (const key of Object.keys(proposed)) {
    // Skip metadata fields
    if (['id', 'created_at', 'last_modified', 'farm_id', 'cycle_id', 'pond_id'].includes(key)) {
      continue;
    }
    if (original[key] !== proposed[key]) {
      changes.push({
        name: key,
        original: original[key],
        proposed: proposed[key],
      });
    }
  }

  return changes;
}

// An application's amount means nothing without its unit; skip the suffix when the edit changes the unit.
function fieldLabel(name: string, unit: string | null, proposed: Record<string, unknown>): string {
  if (name === 'amount' && unit && (proposed.unit === undefined || proposed.unit === unit)) {
    return `Amount (${unit})`;
  }
  return formatFieldName(name);
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'number') return value.toString();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

export function RequestDetailModal({
  request,
  onClose,
  onResolved,
}: RequestDetailModalProps) {
  const [rejectionReason, setRejectionReason] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  const queryClient = useQueryClient();
  const pondName = useTargetPondName(request);
  const target = getTargetDetail(request);

  const approveMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/farms/${request.farm_id}/edit-requests/${request.id}/approve`, {}),
    onSuccess: () => {
      // Approval rewrites the target record, and its readers use many ad-hoc keys: refresh everything
      // except identity data and the immutable cycle -> pond-name map.
      queryClient.invalidateQueries({
        predicate: (query) => !UNAFFECTED_BY_APPROVAL.includes(String(query.queryKey[0])),
      });
      onResolved();
    },
  });

  const rejectMutation = useMutation({
    mutationFn: () =>
      api.post(`/api/v1/farms/${request.farm_id}/edit-requests/${request.id}/reject`, {
        reason: rejectionReason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['edit-requests', request.farm_id] });
      queryClient.invalidateQueries({ queryKey: qk.editRequests.counts(request.farm_id) });
      onResolved();
    },
  });

  const isPending = request.status === 'pending';
  const changedFields = getChangedFields(request.original_data, request.proposed_data);

  return (
    <Dialog open onClose={onClose} title="Edit Request Details" size="lg">
      <div className="space-y-4 max-h-[70vh] overflow-y-auto">
        {/* Request Info */}
        <div className="text-sm text-gray-600 space-y-1">
          <p>
            <span className="font-medium text-gray-700">Requested by:</span>{' '}
            {request.requested_by_name}
          </p>
          <p>
            <span className="font-medium text-gray-700">Requested:</span>{' '}
            {displayPhDate(new Date(request.requested_at))}, {displayPhTime(request.requested_at)}
          </p>
          <p>
            <span className="font-medium text-gray-700">Type:</span>{' '}
            {formatTableName(request.target_table)}
          </p>
          {pondName && (
            <p>
              <span className="font-medium text-gray-700">Pond:</span> {pondName}
            </p>
          )}
          {target.product && (
            <p className="min-w-0 break-words">
              <span className="font-medium text-gray-700">Product:</span> {target.product}
              {target.productCategory && <> ({formatFieldName(target.productCategory)})</>}
            </p>
          )}
          {target.date && (
            <p>
              <span className="font-medium text-gray-700">{target.dateLabel}:</span> {target.date}
            </p>
          )}
          {target.slot && (
            <p>
              <span className="font-medium text-gray-700">Slot:</span> {target.slot}
            </p>
          )}
          {request.reason && (
            <p className="mt-2">
              <span className="font-medium text-gray-700">Reason:</span>{' '}
              <span className="italic">"{request.reason}"</span>
            </p>
          )}
        </div>

        {/* Changes Comparison */}
        <Card className="bg-gray-50 border-gray-200 p-3">
          <h4 className="font-medium text-gray-900 mb-3">Proposed Changes</h4>
          {changedFields.length === 0 ? (
            <p className="text-sm text-gray-500">No changes detected</p>
          ) : (
            <div className="space-y-2">
              {changedFields.map((field) => (
                <div key={field.name} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                  <span className="font-medium text-gray-700 min-w-[120px]">
                    {fieldLabel(field.name, target.unit, request.proposed_data)}:
                  </span>
                  <span className="text-red-600 line-through min-w-0 break-words">
                    {formatValue(field.original)}
                  </span>
                  <ArrowRight className="h-4 w-4 text-gray-400 flex-shrink-0" />
                  <span className="text-green-600 font-medium min-w-0 break-words">
                    {formatValue(field.proposed)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Status badge for resolved requests */}
        {request.status === 'approved' && (
          <Card className="bg-green-50 border-green-200 p-3">
            <p className="text-sm text-green-700">
              <span className="font-medium">Approved</span>
              {request.resolved_by_name && (
                <> by {request.resolved_by_name}</>
              )}
              {request.resolved_at && (
                <> on {displayPhDate(new Date(request.resolved_at))}</>
              )}
            </p>
          </Card>
        )}

        {/* Rejection reason (for already rejected) */}
        {request.status === 'rejected' && request.rejection_reason && (
          <Card className="bg-red-50 border-red-200 p-3">
            <p className="text-sm text-red-700">
              <span className="font-medium">Rejected:</span> {request.rejection_reason}
            </p>
            {request.resolved_by_name && (
              <p className="text-xs text-red-600 mt-1">
                By {request.resolved_by_name}
                {request.resolved_at && (
                  <> on {displayPhDate(new Date(request.resolved_at))}</>
                )}
              </p>
            )}
          </Card>
        )}

        {/* Actions for pending requests */}
        {isPending && !showRejectForm && (
          <div className="flex justify-end gap-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setShowRejectForm(true)}
              disabled={approveMutation.isPending}
            >
              <X className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button
              onClick={() => approveMutation.mutate()}
              disabled={approveMutation.isPending}
            >
              {approveMutation.isPending ? (
                'Approving...'
              ) : (
                <>
                  <Check className="h-4 w-4 mr-2" />
                  Approve
                </>
              )}
            </Button>
          </div>
        )}

        {/* Rejection form */}
        {isPending && showRejectForm && (
          <div className="space-y-3 pt-4 border-t">
            <Textarea
              label="Rejection Reason (required)"
              placeholder="Explain why this edit request is being rejected..."
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              rows={3}
            />
            <div className="flex justify-end gap-3">
              <Button
                variant="outline"
                onClick={() => setShowRejectForm(false)}
                disabled={rejectMutation.isPending}
              >
                Back
              </Button>
              <Button
                variant="danger"
                onClick={() => rejectMutation.mutate()}
                disabled={!rejectionReason.trim() || rejectMutation.isPending}
              >
                {rejectMutation.isPending ? 'Rejecting...' : 'Confirm Rejection'}
              </Button>
            </div>
          </div>
        )}

        {/* Close button for resolved requests */}
        {!isPending && (
          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={onClose}>
              Close
            </Button>
          </div>
        )}
      </div>
    </Dialog>
  );
}
